import { useState, useEffect, useRef, useCallback } from 'react';

/** Loads a consultation's recording as an object URL while `enabled`, and drives seeking into it. */
const useConsultationAudio = ({ consultationId, consultationService, enabled }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioError, setAudioError] = useState(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !consultationId) {
      setAudioUrl(null);
      setAudioLoading(true);
      setAudioError(null);
      return undefined;
    }

    let isCancelled = false;
    let currentAudioUrl = null;

    const loadAudio = async () => {
      try {
        setAudioLoading(true);
        setAudioError(null);
        const audioBlob = await consultationService.fetchConsultationAudio(consultationId);
        if (isCancelled) return;

        currentAudioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(currentAudioUrl);
      } catch (err) {
        console.error('Error loading audio:', err);
        if (!isCancelled) {
          setAudioError('Failed to load audio');
        }
      } finally {
        if (!isCancelled) {
          setAudioLoading(false);
        }
      }
    };
    loadAudio();

    return () => {
      isCancelled = true;
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
      }
    };
  }, [consultationId, consultationService, enabled]);

  const handleSeekAudio = useCallback((time) => {
    audioPlayerRef.current?.seekTo(time);
  }, []);

  return {
    audioUrl,
    audioLoading,
    audioError,
    audioPlayerRef,
    audioCurrentTime,
    setAudioCurrentTime,
    handleSeekAudio,
  };
};

export default useConsultationAudio;
